/**
 * Gmail Authentication Utility
 * Handles Google OAuth 2.0 for Gmail API access
 */

import { doc, getDoc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from './firebase';
import { GmailConnection } from '../types';
import { crmAuthedFetch } from './crmAuthedFetch';

// Google OAuth Configuration
// These should be configured in the Google Cloud Console
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const GOOGLE_REDIRECT_URI = `${window.location.origin}/gmail-callback`;

// Gmail scopes needed for email access.
// `https://mail.google.com/` is required for native Gmail delegation send
// (a delegate sending from the owner's mailbox so Gmail renders "sent by …").
const GMAIL_SCOPES = [
  'https://mail.google.com/',                         // Full mailbox access (delegated send-as)
  'https://www.googleapis.com/auth/gmail.readonly',   // Read emails
  'https://www.googleapis.com/auth/gmail.send',       // Send emails
  'https://www.googleapis.com/auth/gmail.labels',     // Manage funnel labels
  'https://www.googleapis.com/auth/gmail.modify',     // Apply/remove labels, mark read
  'https://www.googleapis.com/auth/userinfo.email',   // Get user email
].join(' ');

/**
 * Check if Gmail is connected for a user
 */
export async function isGmailConnected(userId: string): Promise<boolean> {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) return false;
    
    const data = userDoc.data();
    return !!(data?.gmailConnection?.accessToken && data?.gmailConnection?.refreshToken);
  } catch (error) {
    console.error('Error checking Gmail connection:', error);
    return false;
  }
}

/**
 * Get Gmail connection status and details for a user
 */
export async function getGmailConnection(userId: string): Promise<GmailConnection | null> {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) return null;
    
    const data = userDoc.data();
    const connection = data?.gmailConnection;
    
    if (!connection?.accessToken) return null;
    
    return {
      userId,
      email: connection.email || '',
      accessToken: connection.accessToken,
      refreshToken: connection.refreshToken,
      tokenExpiry: connection.tokenExpiry?.toDate?.() || new Date(),
      lastSyncAt: connection.lastSyncAt?.toDate?.(),
      syncEnabled: connection.syncEnabled ?? true,
      autoLinkEnabled: connection.autoLinkEnabled ?? true,
      connectedAt: connection.connectedAt?.toDate?.() || new Date(),
    };
  } catch (error) {
    console.error('Error getting Gmail connection:', error);
    return null;
  }
}

/**
 * Generate the Google OAuth authorization URL
 */
export function getGoogleAuthUrl(userId: string): string {
  if (!GOOGLE_CLIENT_ID) {
    console.error('Google Client ID not configured');
    return '';
  }
  
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: GOOGLE_REDIRECT_URI,
    response_type: 'code',
    scope: GMAIL_SCOPES,
    access_type: 'offline',
    prompt: 'consent',
    state: userId, // Pass userId to identify the user after redirect
  });
  
  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

/**
 * Initiate Gmail connection (opens OAuth popup or redirects)
 */
export function connectGmail(userId: string): void {
  const authUrl = getGoogleAuthUrl(userId);
  if (!authUrl) {
    alert('Gmail integration is not configured. Please contact support.');
    return;
  }
  
  // Open in a popup window
  const width = 500;
  const height = 600;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;
  
  window.open(
    authUrl,
    'gmail-oauth',
    `width=${width},height=${height},left=${left},top=${top},popup=1`
  );
}

/**
 * Exchange authorization code for tokens (called from callback page)
 * This should ideally be done server-side for security
 */
export async function exchangeCodeForTokens(
  code: string,
  userId: string
): Promise<{ success: boolean; email?: string; error?: string }> {
  try {
    // Call Cloud Function to exchange code (server-side for security)
    const response = await crmAuthedFetch(
      'https://us-central1-lctf-projects.cloudfunctions.net/exchangeGmailCode',
      {
        method: 'POST',
        body: JSON.stringify({ code, userId, redirectUri: GOOGLE_REDIRECT_URI }),
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      return { success: false, error: error.message || 'Failed to connect Gmail' };
    }
    
    const data = await response.json();
    return { success: true, email: data.email };
  } catch (error) {
    console.error('Error exchanging code for tokens:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to connect Gmail',
    };
  }
}

/**
 * Disconnect Gmail account
 */
export async function disconnectGmail(userId: string): Promise<{ success: boolean; error?: string }> {
  try {
    // Get current connection to revoke token
    const connection = await getGmailConnection(userId);
    
    if (connection?.accessToken) {
      // Revoke the token with Google (best effort)
      try {
        await fetch(
          `https://oauth2.googleapis.com/revoke?token=${connection.accessToken}`,
          { method: 'POST' }
        );
      } catch (revokeError) {
        console.warn('Could not revoke Google token:', revokeError);
      }
    }
    
    // Remove connection from Firestore
    await updateDoc(doc(db, 'users', userId), {
      gmailConnection: deleteField(),
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error disconnecting Gmail:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to disconnect Gmail',
    };
  }
}

/**
 * Update Gmail sync settings
 */
export async function updateGmailSettings(
  userId: string,
  settings: { syncEnabled?: boolean; autoLinkEnabled?: boolean }
): Promise<{ success: boolean; error?: string }> {
  try {
    const updates: Record<string, any> = {};
    
    if (settings.syncEnabled !== undefined) {
      updates['gmailConnection.syncEnabled'] = settings.syncEnabled;
    }
    if (settings.autoLinkEnabled !== undefined) {
      updates['gmailConnection.autoLinkEnabled'] = settings.autoLinkEnabled;
    }
    
    await updateDoc(doc(db, 'users', userId), updates);
    return { success: true };
  } catch (error) {
    console.error('Error updating Gmail settings:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update settings',
    };
  }
}

/**
 * Get all connected Gmail accounts (for admin view)
 */
export async function getAllGmailConnections(): Promise<GmailConnection[]> {
  // This would require a more complex query or Cloud Function
  // For now, return empty - can be implemented as needed
  return [];
}

/**
 * Check if Gmail integration is configured (has client ID)
 */
export function isGmailConfigured(): boolean {
  return !!GOOGLE_CLIENT_ID;
}
